// src/pages/VideoCatchupPage.js
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import '../styles/VideoCatchupPage.css';
import { createSlug, findShowBySlug, findEpisodeBySlug } from '../utils/slugify';

// Helpers (keep all existing helper functions)
const parseDateFromTitle = (title) => {
    const dateRegex = /(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})|(\d{4})[\/\-](\d{1,2})[\/\-](\d{1,2})/;
    const match = title && title.match ? title.match(dateRegex) : null;

    if (match) {
        if (match[1]) {
            const day = parseInt(match[1], 10);
            const month = parseInt(match[2], 10) - 1;
            const year = parseInt(match[3], 10);
            const fullYear = year < 100 ? 2000 + year : year;
            return new Date(fullYear, month, day);
        } else {
            const year = parseInt(match[4], 10);
            const month = parseInt(match[5], 10) - 1;
            const day = parseInt(match[6], 10);
            return new Date(year, month, day);
        }
    }
    return new Date(1970, 0, 1);
};

const sortEpisodesByDate = (episodes) => {
    return [...episodes].sort((a, b) => {
        const dateA = parseDateFromTitle(a.title);
        const dateB = parseDateFromTitle(b.title);
        return dateB - dateA;
    });
};

const formatTime = (seconds) => {
    if (isNaN(seconds) || seconds == null) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
};

const formatViews = (v) => {
    if (!v && v !== 0) return '—';
    const n = typeof v === 'number' ? v : parseInt(String(v).replace(/[^0-9]/g, ''), 10) || 0;
    if (n >= 1e9) return (n / 1e9).toFixed(1).replace('.0','') + 'B views';
    if (n >= 1e6) return (n / 1e6).toFixed(1).replace('.0','') + 'M views';
    if (n >= 1e3) return (n / 1e3).toFixed(1).replace('.0','') + 'K views';
    return `${n} view${n !== 1 ? 's' : ''}`;
};

const formatDatePretty = (d) => {
    if (!d) return '';
    const dt = d instanceof Date ? d : new Date(d);
    return dt.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

// Icons
const SearchIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
        <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
    </svg>
);
const CloseIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
        <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
    </svg>
);
const ShareIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
        <path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z" />
    </svg>
);

export const VideoCatchupPage = () => {
    const [shows, setShows] = useState([]);
    const [currentShowIndex, setCurrentShowIndex] = useState(0);
    const [currentEpisodeIndex, setCurrentEpisodeIndex] = useState(-1);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [descExpanded, setDescExpanded] = useState(false);
    const [isDataLoaded, setIsDataLoaded] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    const videoRef = useRef(null);
    const mountedRef = useRef(false);
    const playTimerRef = useRef(null);
    const pendingTimerRef = useRef(null);
    const hasLoadedDataRef = useRef(false);

    const location = useLocation();
    const navigate = useNavigate();
    const { showSlug, episodeSlug } = useParams();

    const CONFIG = {
        dataSource: '/get_videos.php',
        devOrigin: window.location.origin,
        defaultImage: 'https://a8.asurahosting.com/static/uploads/onoradio/album_art.1733486583.png'
    };

    const resolveMediaUrl = useCallback((url) => {
        if (!url) return '';
        try {
            const parsed = new URL(url);
            return parsed.href;
        } catch (e) {
            const base = CONFIG.devOrigin.replace(/\/$/, '');
            if (url.startsWith('/')) return `${base}${url}`;
            return `${base}/${url}`;
        }
    }, [CONFIG.devOrigin]);

    const handleImgError = useCallback((e) => {
        try {
            e.target.onerror = null;
            e.target.src = CONFIG.defaultImage;
        } catch (err) {
            // ignore
        }
    }, [CONFIG.defaultImage]);

    const updateUrl = useCallback((showIndex, episodeIndex) => {
        if (showIndex < 0 || !shows[showIndex]) return;

        const show = shows[showIndex];
        const showSlugVal = createSlug(show.title);

        if (episodeIndex >= 0 && show.episodes && show.episodes[episodeIndex]) {
            const episode = show.episodes[episodeIndex];
            const episodeSlugVal = createSlug(episode.title);
            navigate(`/podcasts/${showSlugVal}/${episodeSlugVal}`, { replace: true });
        } else {
            navigate(`/podcasts/${showSlugVal}`, { replace: true });
        }
    }, [shows, navigate]);

    const loadEpisode = useCallback((showIndex, episodeIndex, autoplay = true) => {
        if (!shows[showIndex] || !shows[showIndex].episodes || !shows[showIndex].episodes[episodeIndex]) return;
        const show = shows[showIndex];
        const episode = show.episodes[episodeIndex];

        setCurrentShowIndex(showIndex);
        setCurrentEpisodeIndex(episodeIndex);
        setDescExpanded(false);

        updateUrl(showIndex, episodeIndex);

        if (!videoRef.current) return;

        if (playTimerRef.current) {
            clearTimeout(playTimerRef.current);
            playTimerRef.current = null;
        }

        try {
            videoRef.current.pause();

            const videoUrl = episode.video ? episode.video : '';
            const posterUrl = episode.thumbnail || episode.image || show.image || CONFIG.defaultImage;

            videoRef.current.crossOrigin = 'anonymous';
            videoRef.current.preload = 'metadata';
            videoRef.current.playsInline = true;

            videoRef.current.src = videoUrl;
            videoRef.current.poster = posterUrl;
            videoRef.current.load();
        } catch (err) {
            console.warn('Error setting video src:', err);
            return;
        }

        if (autoplay) {
            playTimerRef.current = setTimeout(() => {
                playTimerRef.current = null;
                if (!mountedRef.current || !videoRef.current) return;

                const playPromise = videoRef.current.play();
                if (playPromise && playPromise.catch) {
                    playPromise.catch(err => {
                        if (!mountedRef.current) return;
                        console.warn('Video play() failed:', err);
                    });
                }
            }, 150);
        }
    }, [shows, updateUrl, CONFIG.defaultImage]);

    const handleSelectShow = useCallback((index) => {
        setCurrentShowIndex(index);
        setCurrentEpisodeIndex(-1);
        updateUrl(index, -1);
    }, [updateUrl]);

    const prevEpisode = useCallback(() => {
        const show = shows[currentShowIndex];
        if (!show?.episodes?.length) return;
        if (currentEpisodeIndex < show.episodes.length - 1) {
            loadEpisode(currentShowIndex, currentEpisodeIndex + 1);
        } else if (currentShowIndex > 0) {
            const newShowIndex = currentShowIndex - 1;
            loadEpisode(newShowIndex, 0);
        }
    }, [currentShowIndex, currentEpisodeIndex, shows, loadEpisode]);

    const nextEpisode = useCallback(() => {
        const show = shows[currentShowIndex];
        if (!show?.episodes?.length) return;
        if (currentEpisodeIndex > 0) {
            loadEpisode(currentShowIndex, currentEpisodeIndex - 1);
        } else if (currentShowIndex < shows.length - 1) {
            const newShowIndex = currentShowIndex + 1;
            loadEpisode(newShowIndex, 0);
        }
    }, [currentShowIndex, currentEpisodeIndex, shows, loadEpisode]);

    const loadShowsData = useCallback(async () => {
        if (hasLoadedDataRef.current) {
            console.log('[VideoCatchup] Data already loaded, skipping');
            return;
        }

        hasLoadedDataRef.current = true;
        setIsLoading(true);
        setError(null);

        try {
            console.log('[VideoCatchup] Fetching from:', CONFIG.dataSource);

            const response = await fetch(CONFIG.dataSource, {
                headers: {
                    'Accept': 'application/json',
                    'Cache-Control': 'no-cache'
                }
            });

            console.log('[VideoCatchup] Response status:', response.status);

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const data = await response.json();
            console.log('[VideoCatchup] Data received:', data);

            if (data.error) {
                console.error('[VideoCatchup] Server error:', data.error);
                setError(`Server error: ${data.error}`);
                setIsLoading(false);
                return;
            }

            let showsData = data.shows || [];
            console.log('[VideoCatchup] Shows found:', showsData.length);

            if (showsData.length === 0) {
                console.warn('[VideoCatchup] No shows found');
                setError('No shows found. Please check if video files exist in the correct directory.');
                setIsLoading(false);
                return;
            }

            const normalized = showsData.map(show => {
                const safeShow = { ...show };
                if (safeShow.image) safeShow.image = resolveMediaUrl(safeShow.image);
                if (safeShow.episodes && Array.isArray(safeShow.episodes)) {
                    safeShow.episodes = safeShow.episodes.map(ep => ({
                        ...ep,
                        thumbnail: ep.thumbnail ? resolveMediaUrl(ep.thumbnail) : (ep.image ? resolveMediaUrl(ep.image) : ''),
                        image: ep.image ? resolveMediaUrl(ep.image) : '',
                        video: ep.video ? resolveMediaUrl(ep.video) : ''
                    }));
                }
                return safeShow;
            });

            setShows(normalized);
            setIsDataLoaded(true);

            let targetShowIndex = 0;
            let targetEpisodeIndex = -1;
            let shouldAutoplay = false;

            if (showSlug && normalized.length > 0) {
                const foundShowIndex = findShowBySlug(normalized, showSlug);
                if (foundShowIndex >= 0) {
                    targetShowIndex = foundShowIndex;

                    if (episodeSlug && normalized[foundShowIndex]?.episodes) {
                        const foundEpisodeIndex = findEpisodeBySlug(
                            normalized[foundShowIndex].episodes,
                            episodeSlug
                        );
                        if (foundEpisodeIndex >= 0) {
                            targetEpisodeIndex = foundEpisodeIndex;
                            shouldAutoplay = true;
                        }
                    }
                }
            } else {
                const params = new URLSearchParams(location.search);
                const showParam = params.get('show');
                const episodeParam = params.get('episode');
                const autoplayParam = params.get('autoplay') === 'true';

                if (showParam && normalized.length > 0) {
                    const foundIndex = normalized.findIndex(show =>
                        show.title.toLowerCase().includes(showParam.toLowerCase()) ||
                        showParam.toLowerCase().includes(show.title.toLowerCase())
                    );
                    if (foundIndex >= 0) targetShowIndex = foundIndex;
                }

                if (episodeParam && normalized[targetShowIndex]?.episodes) {
                    const episodes = normalized[targetShowIndex].episodes;
                    const foundEpisodeIndex = episodes.findIndex(ep =>
                        ep.title.toLowerCase().includes(episodeParam.toLowerCase())
                    );
                    if (foundEpisodeIndex >= 0) {
                        targetEpisodeIndex = foundEpisodeIndex;
                        shouldAutoplay = autoplayParam;
                    }
                }
            }

            setCurrentShowIndex(targetShowIndex);

            if (targetEpisodeIndex >= 0) {
                if (pendingTimerRef.current) {
                    clearTimeout(pendingTimerRef.current);
                    pendingTimerRef.current = null;
                }
                pendingTimerRef.current = setTimeout(() => {
                    pendingTimerRef.current = null;
                    if (mountedRef.current) {
                        loadEpisode(targetShowIndex, targetEpisodeIndex, shouldAutoplay);
                    }
                }, 150);
            } else {
                if (showSlug) {
                    updateUrl(targetShowIndex, -1);
                }
            }

        } catch (err) {
            console.error('[VideoCatchup] Error loading shows:', err);
            setError(`Failed to load shows: ${err.message}`);
        } finally {
            setIsLoading(false);
        }
    }, [location.search, showSlug, episodeSlug, updateUrl, loadEpisode, resolveMediaUrl]);

    useEffect(() => {
        const v = videoRef.current;
        if (!v) return;

        let countedKey = null;

        const onPlay = () => {
            const show    = shows[currentShowIndex];
            const episode = show?.episodes?.[currentEpisodeIndex];
            if (!show || !episode) return;
            if (!show.folder || !episode.folder) return;

            const key = `${show.folder}/${episode.folder}`;
            if (countedKey === key) return;
            countedKey = key;

            fetch('/admin/video_catchup/track_video_play.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    show_folder:    show.folder,
                    episode_folder: episode.folder,
                }),
            }).catch(() => { /* silent */ });
        };

        const onEnded          = () => nextEpisode();
        const onError          = () => console.error('Video element error object:', v.error);
        const onLoadedMetadata = () => console.log('Video metadata loaded, duration:', v.duration);
        const onCanPlay        = () => console.log('Video can play');

        v.addEventListener('play', onPlay);
        v.addEventListener('ended', onEnded);
        v.addEventListener('error', onError);
        v.addEventListener('loadedmetadata', onLoadedMetadata);
        v.addEventListener('canplay', onCanPlay);

        return () => {
            v.removeEventListener('play', onPlay);
            v.removeEventListener('ended', onEnded);
            v.removeEventListener('error', onError);
            v.removeEventListener('loadedmetadata', onLoadedMetadata);
            v.removeEventListener('canplay', onCanPlay);
        };
    }, [nextEpisode, shows, currentShowIndex, currentEpisodeIndex]);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;

            if (playTimerRef.current) {
                clearTimeout(playTimerRef.current);
                playTimerRef.current = null;
            }
            if (pendingTimerRef.current) {
                clearTimeout(pendingTimerRef.current);
                pendingTimerRef.current = null;
            }

            if (videoRef.current) {
                try {
                    videoRef.current.pause();
                    videoRef.current.removeAttribute('src');
                    videoRef.current.load();
                } catch (e) {
                    // ignore
                }
            }
        };
    }, []);

    useEffect(() => {
        loadShowsData();
    }, [loadShowsData]);

    const currentShow = useMemo(() => shows[currentShowIndex] || {}, [shows, currentShowIndex]);
    const sortedEpisodes = useMemo(() => {
        return currentShow.episodes ? sortEpisodesByDate(currentShow.episodes) : [];
    }, [currentShow]);
    const activeEpisode = useMemo(() => {
        return currentShow.episodes?.[currentEpisodeIndex] || null;
    }, [currentShow, currentEpisodeIndex]);

    // Filter shows by search (title or any contained episode title)
    const filteredShows = useMemo(() => {
        if (!searchQuery.trim()) return shows;
        const q = searchQuery.toLowerCase().trim();
        return shows.filter(show => {
            if (show.title.toLowerCase().includes(q)) return true;
            return show.episodes?.some(ep => ep.title.toLowerCase().includes(q));
        });
    }, [shows, searchQuery]);

    return (
        <div className="pageContainer">
            <section className="news-page-header">
                <h1>Podcasts</h1>
                <p>Watch our video podcasts on demand!</p>
            </section>

            <section className="video-player-widget yt-style">

                {/* Search — matches CatchupPage styling */}
                <div className="catchup-search-wrap">
                    <div className="catchup-search-bar">
                        <input
                            className="catchup-search-input"
                            placeholder="SEARCH SHOWS OR EPISODES"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                        {searchQuery ? (
                            <button
                                type="button"
                                className="catchup-search-submit"
                                onClick={() => setSearchQuery('')}
                                aria-label="Clear search"
                            >
                                <CloseIcon />
                            </button>
                        ) : (
                            <span className="catchup-search-icon-static" aria-hidden>
                                <SearchIcon />
                            </span>
                        )}
                    </div>
                </div>

                {/* Video left, meta right */}
                <div className="video-top-section">
                    <div className="video-column">
                        <div className="video-wrap">
                            <div className="video-aspect">
                                <video
                                    ref={videoRef}
                                    controls
                                    className="video-element"
                                    playsInline
                                    preload="metadata"
                                >
                                    Your browser does not support HTML5 video.
                                </video>
                            </div>
                        </div>
                    </div>

                    <div className="video-meta-block">
                        <h2 className="video-title">
                            {activeEpisode ? activeEpisode.title : (isLoading ? 'Loading...' : 'Select an episode to play')}
                        </h2>

                        <div className="video-stats-row">
                            <div className="left-stats"></div>
                            <div className="right-actions">
                                <button
                                    className="icon-btn share"
                                    onClick={() => {
                                        if (activeEpisode) {
                                            const shareUrl = `${window.location.origin}/podcasts/${createSlug(currentShow.title)}/${createSlug(activeEpisode.title)}`;
                                            navigator.clipboard.writeText(shareUrl).then(() => {
                                                alert('Link copied to clipboard!');
                                            });
                                        }
                                    }}
                                >
                                    <ShareIcon /> Share
                                </button>
                            </div>
                        </div>

                        <div className="channel-row">
                            <div className="channel-left">
                                <img
                                    className="channel-avatar"
                                    src={currentShow.image || CONFIG.defaultImage}
                                    alt={currentShow.title}
                                    onError={handleImgError}
                                />
                                <div className="channel-info">
                                    <div className="channel-name">
                                        {currentShow.title || 'Unknown Show'}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="description-block">
                            <div className={`desc-text ${descExpanded ? 'expanded' : ''}`}>
                                {activeEpisode ? (activeEpisode.description || currentShow.description || 'No description available.') : 'Select an episode to see the description.'}
                            </div>
                            {activeEpisode && (activeEpisode.description || currentShow.description) && (
                                <button className="desc-toggle" onClick={() => setDescExpanded(!descExpanded)}>
                                    {descExpanded ? 'Show less' : 'Show more'}
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Drilldown: Shows left, Episodes right */}
                {isLoading ? (
                    <div className="catchup-loading">
                        <div className="spinner" />
                        <p>Loading shows…</p>
                    </div>
                ) : error ? (
                    <div className="catchup-empty">{error}</div>
                ) : filteredShows.length === 0 ? (
                    <div className="catchup-empty">
                        {searchQuery
                            ? `No shows matching "${searchQuery}"`
                            : 'No shows found.'}
                    </div>
                ) : (
                    <div className="catchup-drilldown">
                        {/* Shows sidebar */}
                        <aside className="catchup-shows-sidebar">
                            <div className="catchup-shows-header">
                                <span>Shows</span>
                                <span className="catchup-shows-count">{filteredShows.length}</span>
                            </div>
                            <ul className="catchup-shows-list">
                                {filteredShows.map((show) => {
                                    const idx = shows.indexOf(show);
                                    return (
                                        <li
                                            key={`show-${idx}-${show.title}`}
                                            className={`catchup-show-item ${idx === currentShowIndex ? 'active' : ''}`}
                                            onClick={() => handleSelectShow(idx)}
                                        >
                                            <img
                                                src={show.image || CONFIG.defaultImage}
                                                alt={show.title}
                                                className="catchup-show-img"
                                                onError={handleImgError}
                                            />
                                            <div className="catchup-show-info">
                                                <h4 className="catchup-show-title">{show.title}</h4>
                                                <p className="catchup-show-count">
                                                    {show.episodes
                                                        ? `${show.episodes.length} episode${show.episodes.length !== 1 ? 's' : ''}`
                                                        : 'No episodes'}
                                                </p>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        </aside>

                        {/* Episodes panel */}
                        <div className="catchup-episodes-panel">
                            <div className="catchup-episodes-header">
                                <h3>
                                    Episodes in <span id="selected-show-name">{currentShow.title || 'Select a show'}</span>
                                </h3>
                                <span className="catchup-episodes-count" id="episode-count">
                                    {currentShow.episodes
                                        ? `${currentShow.episodes.length} ep${currentShow.episodes.length !== 1 ? 's' : ''}`
                                        : '0 eps'}
                                </span>
                            </div>

                            {!currentShow.episodes || currentShow.episodes.length === 0 ? (
                                <div className="catchup-empty-small">
                                    Select a show to see episodes
                                </div>
                            ) : (
                                <ul className="catchup-episodes-list">
                                    {sortedEpisodes.map((episode, idx) => {
                                        const originalIndex = currentShow.episodes.indexOf(episode);
                                        const isActive = originalIndex === currentEpisodeIndex;
                                        const duration = episode.duration || (episode.length_seconds ? formatTime(episode.length_seconds) : '');

                                        return (
                                            <li
                                                key={`episode-${idx}-${episode.title}`}
                                                className={`catchup-episode-item ${isActive ? 'active' : ''}`}
                                                onClick={() => loadEpisode(currentShowIndex, originalIndex, true)}
                                            >
                                                <div className="catchup-episode-thumb-wrap">
                                                    <img
                                                        src={episode.thumbnail || episode.image || currentShow.image || CONFIG.defaultImage}
                                                        alt={episode.title}
                                                        className="catchup-episode-thumb"
                                                        onError={handleImgError}
                                                    />
                                                    {duration && <span className="catchup-episode-duration">{duration}</span>}
                                                </div>
                                                <div className="catchup-episode-info">
                                                    <h5 className="catchup-episode-title">{episode.title}</h5>
                                                    <p className="catchup-episode-meta">
                                                        {episode.description
                                                            ? episode.description.substring(0, 140) + (episode.description.length > 140 ? '…' : '')
                                                            : 'No description'}
                                                    </p>
                                                </div>
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </div>
                    </div>
                )}
            </section>
        </div>
    );
};

export default VideoCatchupPage;